import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { BIMPLUS_API } from '@web-portal/domains/shared/bimplus-core';
import { RxRequestOptions } from '../../';

/**
 * Response from the token info API endpoint.
 * Contains information about a validated access token.
 */
export interface TokenInfoResponse {
  /** The client ID (audience) associated with the token */
  audience: string;
  /** The remaining time in seconds until the token expires */
  expires_in: number;
  /** The user ID associated with the token */
  user_id: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationAdapter {
  private readonly api = inject(BIMPLUS_API);
  private readonly translateService = inject(TranslateService);

  /**
   * Gets information about the provided access token.
   *
   * Validates the token and returns details including audience, expiration time,
   * and user ID. Returns a 400 error if the token is invalid or signature validation fails.
   *
   * @param accessToken - The access token to get information about
   * @returns Observable with token info response containing audience, expiresIn, and userId
   */
  getTokenInfo(accessToken: string): Observable<TokenInfoResponse> {
    const options: RxRequestOptions = {
      successMessage: {
        severity: 'success',
        summary: this.translateService.instant('auth.authorization-successful'),
        detail: this.translateService.instant('auth.token-validated-successfully'),
        life: 1500,
      },
      errorMessages: {
        '400': {
          severity: 'error',
          summary: this.translateService.instant('auth.authorization-failed'),
          detail: this.translateService.instant('auth.token-validation-failed'),
          life: 3000,
        },
      },
    };

    const url = `${this.api.getApiUrl()}tokeninfo?access_Token=${encodeURIComponent(accessToken)}`;
    return this.api.rxRequest(this.api.request.get<TokenInfoResponse>(url), options);
  }
}
